import PropTypes from "prop-types";

import ActionButton from "../formInputs/buttons/ActionButton";
import Legend from "./Legend";
import studentDisplayName from "../../utils/studentDisplayName";
import StudentImage from "../studentInfoBlock/StudentImage";
import TabGroup from "./TabGroup";
import T from "../translations/Translations";

import "../../styles/StudentInfoBlock.scss";

const DisplayEprStudentInfo = ({ activeTab, downloadPdf, epr, eprs, handleChangeEpr, loader, tabs }) => {
    const {
        gradeName,
        gradingProfileId,
        locationName,
        pdfProcessed,
        schoolYearName,
        studentName,
        studentNumber,
        teacherName
    } = epr;

    const notReady = T({ key: "Student_Info_Not_Ready" });
    /**
     * Creates a student info object by robustly parsing a name string
     * expected to be in the format "LastName, FirstName".
     * @name getStudentInfo
     * @param {string | undefined} studentName - The student's full name string.
     * @param {string} studentNumber - The student's ID number.
     * @returns {{}} An object containing firstName, lastName, and studentNumber.
     */
    const getStudentInfo = (studentName, studentNumber) => {
        // Default to an array that prevents errors if split returns less than 2 elements.
        // If studentName is missing or not a string, we default to an array of two empty strings.
        const nameParts =
            typeof studentName === "string" && studentName.includes(", ") ? studentName.split(", ") : ["", ""];

        const [lastName = "", firstName = ""] = nameParts;

        const studentInfo = {
            firstName,
            lastName,
            studentNumber
        };

        return studentInfo;
    };

    const studentInfo = getStudentInfo(studentName, studentNumber);
    const selectedSemester = T({ key: "Semester_Selected" });

    return (
        <div className="guardian-student-container">
            <div className="guardian-student-info-container">
                <div className="epr-student-info">
                    <div className="photo-container">
                        <StudentImage
                            className="rounded-3"
                            height={130}
                            noShift
                            setLoader={() => {
                                return;
                            }}
                            studentInfo={studentInfo}
                            width={88}
                        />
                    </div>
                    <div>
                        <div className="epr-student-name">
                            {studentDisplayName(studentName)} ({studentNumber || "Not Available"}) - Report Card
                        </div>
                        <div>
                            <span className="epr-student-info-label">{T({ key: "Student_Info_School" })}:</span>{" "}
                            {locationName}
                        </div>
                        <div>
                            <span className="epr-student-info-label">{T({ key: "Student_Info_Teacher" })}:</span>{" "}
                            {teacherName}
                        </div>
                        <div>
                            <span className="epr-student-info-label">{T({ key: "Student_Info_School_Year" })}:</span>{" "}
                            {schoolYearName}
                        </div>
                        <div>
                            <span className="epr-student-info-label">{T({ key: "Student_Info_Grade" })}:</span>{" "}
                            {gradeName}
                        </div>
                    </div>
                </div>
                <div className="guardian-student-info-right">
                    <div className="guardian-view-title">
                        {epr.semester} | {schoolYearName} Report Card
                    </div>
                    <div>
                        <ActionButton
                            disable={loader || pdfProcessed !== "Y"}
                            label={T({ key: "Most_Recent_Pdf_Download" })}
                            onClick={() => downloadPdf(gradingProfileId, schoolYearName, false)}
                        />
                    </div>
                    {pdfProcessed !== "Y" && <div className="epr-download-note">{notReady}</div>}
                </div>
            </div>
            <Legend />
            {eprs?.length > 1 && (
                <div className="guardian-semester-select">
                    <div className="guardian-tab-title">{selectedSemester}</div>
                    <TabGroup activeTab={activeTab} handleClick={handleChangeEpr} tabs={tabs} />
                </div>
            )}
        </div>
    );
};

DisplayEprStudentInfo.propTypes = {
    activeTab: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    downloadPdf: PropTypes.func.isRequired,
    epr: PropTypes.oneOfType([PropTypes.object]).isRequired,
    eprs: PropTypes.instanceOf(Array).isRequired,
    handleChangeEpr: PropTypes.func.isRequired,
    loader: PropTypes.bool.isRequired,
    tabs: PropTypes.instanceOf(Array).isRequired
};

export default DisplayEprStudentInfo;
